import { create } from 'zustand'
import { adminApi, MyPermissions } from '../lib/api/admin'
import { useAuthStore } from './authStore'

interface PermissionState {
  loaded: boolean
  is_super_admin: boolean
  hierarchy_level: number
  permissions: MyPermissions['permissions']
  load: () => Promise<void>
  clear: () => void
  can: (module: string, action: 'view' | 'create' | 'edit' | 'delete') => boolean
}

export const usePermissionStore = create<PermissionState>((set, get) => ({
  loaded: false,
  is_super_admin: false,
  hierarchy_level: 0,
  permissions: {},

  load: async () => {
    try {
      const data = await adminApi.getMyPermissions()
      set({
        loaded: true,
        is_super_admin: data.is_super_admin,
        hierarchy_level: data.hierarchy_level ?? 0,
        permissions: data.permissions,
      })
    } catch {
      // Non-admin users or network error — treat as no permissions loaded
      set({ loaded: true, is_super_admin: false, hierarchy_level: 0, permissions: {} })
    }
  },

  clear: () => set({ loaded: false, is_super_admin: false, hierarchy_level: 0, permissions: {} }),

  can: (module, action) => {
    const { is_super_admin, permissions } = get()
    if (is_super_admin) return true
    return permissions[module]?.[`can_${action}`] ?? false
  },
}))

// Auto-clear whenever the auth token is removed (explicit logout OR 401 auto-redirect).
// permissionStore → authStore is safe; the reverse is not (would be circular via admin → client → authStore).
useAuthStore.subscribe((state, prevState) => {
  if (prevState.token !== null && state.token === null) {
    usePermissionStore.getState().clear()
  }
})
